import { uiText } from "@/lib/i18n/uiText";
import { Button } from "@/components/ui/button";
import { Coins, Loader2, Lock, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { usePredictionGate, type GateKind } from "@/hooks/usePredictionGate";

interface RevealGateProps {
  /**
   * Prediction id (any sport — unlocks are keyed by id, not by table).
   * Omit only for ephemeral picks that have no persistent row (Safe Bet
   * portal), in which case the gate falls back to a sign-in requirement.
   */
  predictionId?: string | undefined;
  /** Already-played picks are never gated (cards only — analysis always is). */
  evaluated?: boolean;
  /** Kickoff ISO time — once passed, pick and analysis are free. */
  kickoff?: string | null | undefined;
  /** "card" gates the selection; "analysis" gates reasoning/strategy content. */
  kind?: GateKind;
  className?: string;
  children: React.ReactNode;
}

/**
 * The one gate every sport and every surface shares.
 *
 * - Played match cards are free for everyone.
 * - Upcoming cards: one free reveal per UTC day, then 2 credits.
 * - Analysis: always 1 credit, played or upcoming.
 * - Crawler open-access ON reveals everything so bots index real content.
 */
export function RevealGate({
  predictionId,
  evaluated = false,
  kickoff,
  kind = "card",
  className,
  children,
}: RevealGateProps) {
  const gate = usePredictionGate(predictionId, { kind, evaluated, kickoff });
  if (!gate.locked) return <>{children}</>;

  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    gate.act();
  };

  return (
    <div className={cn("relative min-h-32", className)}>
      {/* Locked content is never mounted — only neutral placeholder bars, so
          neither the page, DevTools nor screen readers can read the pick. */}
      <div aria-hidden="true" className="pointer-events-none select-none space-y-2 p-3 opacity-40">
        <div className="h-3 w-2/3 rounded bg-muted" />
        <div className="h-5 w-1/2 rounded bg-muted" />
        <div className="h-3 w-1/3 rounded bg-muted" />
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-lg bg-background/70 p-3 text-center">
        <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Lock className="h-3 w-3" aria-hidden="true" />
          {kind === "analysis" ? uiText("Locked analysis") : uiText("Locked pick")}
        </p>
        <Button size="sm" className="min-h-11 px-4" onClick={onClick} disabled={gate.pending}>
          {gate.pending ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : gate.freeAvailable ? (
            <Sparkles className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <Coins className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          )}
          {uiText(gate.label)}
        </Button>
        {/* The price of this single action, always visible so a visitor knows
            the cost of one pick before they commit. */}
        <p className="text-[11px] text-muted-foreground">
          {gate.freeAvailable
            ? uiText("Free now · then {{v0}} per {{v1}}", { v0: gate.priceLabel, v1: gate.kind === "analysis" ? "analysis" : "pick" })
            : uiText("{{v0}} for this {{v1}}", { v0: gate.priceLabel, v1: gate.kind === "analysis" ? "analysis" : "pick" })}
        </p>
      </div>

    </div>
  );
}

/**
 * Analysis-only convenience wrapper — reasoning, strategy signals, EV
 * breakdowns and "why this call" blocks. Always priced at 1 credit, for every
 * sport, whether the match has been played or not.
 */
export function AnalysisGate(props: Omit<RevealGateProps, "kind">) {
  return <RevealGate {...props} kind="analysis" />;
}
